import { AlertTriangle, CloudOff, Lock } from 'lucide-react';
import { LOCK_MESSAGES, STALE_WARNING_DAYS, cloudMirrorMessage, onlineCopyFreshness, staleWarningMessage } from '@his/shared';
import { useAuth } from '@/lib/auth';

/**
 * Offline distribution (docs/SYNC.md; plan §3.5): the one-line strip every
 * staff screen shows when this install is read-only, or about to be.
 *
 *  - red, locked: no check-in inside the grace period, a machine mismatch,
 *    or a required update — the fixed copy from the plan, not paraphrased;
 *  - red, cloud mirror: this is the cloud and the hospital writes on its own
 *    local server;
 *  - amber, warning: the countdown has reached the last three days.
 */
export function LicenseLockBanner() {
  const { user } = useAuth();
  if (!user || user.isPlatformUser) return null;

  const lockState = user.lockState ?? 'NONE';
  const reason = user.readOnlyReason ?? null;
  const daysUntilLock = user.daysUntilLock ?? null;

  if (reason === 'cloud_mirror') {
    const freshness = onlineCopyFreshness(user.mirrorCurrentAsOf, user.mirrorServerSeenAt);
    const tone = freshness.behind ? 'border-warning/30 bg-warning/10 text-warning' : 'border-destructive/30 bg-destructive/10 text-destructive';
    return (
      <div role="alert" className={`flex items-center justify-center gap-2 border-b px-4 py-2 text-xs font-medium no-print ${tone}`}>
        <CloudOff className="h-3.5 w-3.5 flex-none" aria-hidden />
        <span>
          {cloudMirrorMessage(user.facilityType)}
          {freshness.asOf ? ` The online copy is current as of ${freshness.asOf.toLocaleString()}.` : ''}
        </span>
      </div>
    );
  }

  if (lockState !== 'NONE') {
    return (
      <div role="alert" className="flex items-center justify-center gap-2 border-b border-destructive/30 bg-destructive/10 px-4 py-2 text-xs font-medium text-destructive no-print">
        <Lock className="h-3.5 w-3.5 flex-none" aria-hidden />
        {LOCK_MESSAGES[lockState]}
      </div>
    );
  }

  if (daysUntilLock !== null && daysUntilLock <= STALE_WARNING_DAYS) {
    return (
      <div role="status" className="flex items-center justify-center gap-2 border-b border-warning/30 bg-warning/10 px-4 py-2 text-xs font-medium text-warning no-print">
        <AlertTriangle className="h-3.5 w-3.5 flex-none" aria-hidden />
        {staleWarningMessage(daysUntilLock)}
      </div>
    );
  }

  return null;
}
